<template>
  <div class="order-seven-trend-chart">
    <div class="chart-header">
      <h3 class="chart-title">近七日平台交易金额</h3>
      <a-radio-group v-model="orderType" button-style="solid" size="small" @change="handleTypeChange">
        <a-radio-button value="0">代收</a-radio-button>
        <a-radio-button value="1">代付</a-radio-button>
      </a-radio-group>
    </div>

    <div class="chart-container" ref="chartContainer" :style="{ height: '300px' }">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <a-spin size="large" tip="图表加载中..." />
      </div>
      <!-- 无数据状态 -->
      <div v-else-if="!hasData" class="no-data-container">
        <div class="no-data-text">暂无数据</div>
        <!-- <a-empty description="暂无数据" /> -->
      </div>
    </div>
  </div>
</template>

<script>
import { getChartComponent } from '@/utils/chartLoader'
import { formatAmount } from '@/utils/util'

export default {
  name: 'OrderSevenTrend',
  props: {
    chartData: {
      type: [Object, Array],
      default: () => []
    }
  },
  data () {
    return {
      orderType: '0',
      chart: null,
      loading: false,
      Column: null // 动态加载的图表类，从Line改为Column
    }
  },
  computed: {
    hasData () {
      // 处理chartData可能是数组或对象的情况
      if (Array.isArray(this.chartData)) {
        return this.chartData.length > 0
      }
      // 如果是对象，检查是否有sevenDaysData属性
      const data = this.chartData || {}
      const sevenDaysData = data.sevenDaysData || []
      return sevenDaysData.length > 0
    }
  },
  watch: {
    chartData: {
      handler (newData) {
        console.log('OrderSevenTrend chartData changed:', newData)
        if (newData && Object.keys(newData).length > 0) {
          this.$nextTick(() => {
            this.renderChart()
          })
        }
      },
      deep: true,
      immediate: true
    }
  },
  async mounted () {
    // 动态导入G2Plot
    await this.loadG2Plot()
    this.$nextTick(() => {
      this.initChart()
    })
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resizeChart)
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  },
  methods: {
    // 动态加载G2Plot
    async loadG2Plot () {
      try {
        console.log('开始加载G2Plot Column for SevenTrend...')
        this.Column = await getChartComponent('Column')
        console.log('G2Plot Column for SevenTrend加载成功')
      } catch (error) {
        console.error('G2Plot Column for SevenTrend加载失败:', error)
        this.$message.error('图表库加载失败')
      }
    },

    // 初始化图表
    initChart () {
      if (!this.$refs.chartContainer || !this.Column) {
        console.warn('OrderSevenTrend chart container not found or G2Plot not loaded')
        return
      }
      this.renderChart()
    },

    // 渲染图表
    async renderChart () {
      try {
        if (!this.$refs.chartContainer || !this.Column) {
          console.warn('OrderSevenTrend chart container not found or G2Plot not loaded, skip rendering')
          return
        }

        if (this.chart) {
          this.chart.destroy()
          this.chart = null
        }

        this.loading = true
        // 处理数据格式，确保日期格式正确
        let chartData = []
        let sevenDaysData = []

        // 处理不同类型的chartData
        if (Array.isArray(this.chartData)) {
          sevenDaysData = this.chartData
        } else {
          const data = this.chartData || {}
          sevenDaysData = data.sevenDaysData || []
        }

        console.log('Rendering OrderSevenTrend chart with data:', sevenDaysData)

        if (sevenDaysData.length > 0) {
          chartData = sevenDaysData.map(item => {
            // 确保从API返回的数据格式正确
            const day = item.day || item.date || ''
            const amountSum = parseFloat(item.amountSum || item.amount || 0)
            return {
              date: this.formatDate(day),
              amount: amountSum,
              formattedAmount: formatAmount(amountSum)
            }
          })
        } else {
          // 如果没有数据，生成近7天的空数据
          const today = new Date()
          for (let i = 6; i >= 0; i--) {
            const date = new Date(today)
            date.setDate(date.getDate() - i)
            const dateStr = date.toISOString().split('T')[0]
            chartData.push({
              date: this.formatDate(dateStr),
              amount: 0,
              formattedAmount: '₹0'
            })
          }
        }

        console.log('Final OrderSevenTrend chart data:', chartData)

        // 确保容器存在且有尺寸
        if (!this.$refs.chartContainer || this.$refs.chartContainer.offsetWidth === 0) {
          console.warn('OrderSevenTrend chart container has no width, retrying...')
          setTimeout(() => this.renderChart(), 100)
          return
        }

        this.chart = new this.Column(this.$refs.chartContainer, {
          data: chartData,
          xField: 'date',
          yField: 'amount',
          columnStyle: {
            radius: [4, 4, 0, 0], // 柱状图顶部圆角
            fill: '#1890ff'
          },
          tooltip: {
            showMarkers: false,
            formatter: (datum) => {
              return {
                name: '交易金额',
                value: datum.formattedAmount
              }
            },
            customContent: (title, items) => {
              const item = items[0]
              if (!item) return ''

              return `
                <div style="padding: 12px; border-radius: 6px; font-size: 12px;">
                  <div style="margin-bottom: 4px; font-weight: bold;">${title}</div>
                  <div style="display: flex; align-items: center;">
                    <span style=""></span>
                    <span>交易金额: ${item.data.formattedAmount}</span>
                  </div>
                </div>
              `
            }
          },
          yAxis: {
            title: {
              text: '交易金额',
              style: {
                fontSize: 12,
                fill: '#666'
              }
            },
            grid: {
              line: {
                style: {
                  stroke: '#f0f0f0',
                  lineWidth: 1,
                  lineDash: [4, 4]
                }
              }
            },
            label: {
              formatter: (text) => {
                return this.formatAmount(parseFloat(text))
              },
              style: {
                fontSize: 11,
                fill: '#666'
              }
            }
          },
          xAxis: {
            title: {
              text: '日期',
              style: {
                fontSize: 12,
                fill: '#666'
              }
            },
            label: {
              autoRotate: false,
              style: {
                fontSize: 11,
                fill: '#666'
              }
            }
          },
          label: {
            position: 'top',
            style: {
              fontSize: 10,
              fill: '#666'
            },
            formatter: (text) => {
              return text > 0 ? this.formatAmount(text) : ''
            }
          },
          annotations: [
            // 添加零线
            {
              type: 'line',
              start: ['min', 0],
              end: ['max', 0],
              style: {
                stroke: '#d9d9d9',
                lineWidth: 1
              }
            }
          ],
          animation: {
            appear: {
              animation: 'fade-in',
              duration: 800
            }
          }
        })

        this.chart.render()
        this.loading = false
        console.log('OrderSevenTrend chart rendered successfully')
      } catch (error) {
        this.loading = false
        console.error('Error rendering OrderSevenTrend chart:', error)
        this.$message.error('七日趋势图表渲染失败')
      }
    },

    // 格式化日期
    formatDate (dateStr) {
      try {
        const date = new Date(dateStr)
        if (isNaN(date.getTime())) {
          console.warn('Invalid date:', dateStr)
          return dateStr
        }
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        return `${month}-${day}`
      } catch (error) {
        console.error('Error formatting date:', error)
        return dateStr
      }
    },

    // 格式化金额
    formatAmount (amount) {
      try {
        const numAmount = Number(amount)
        if (isNaN(numAmount) || numAmount === 0) return '₹0'
        if (numAmount >= 10000) {
          return '₹' + (numAmount / 10000).toFixed(1) + '万'
        } else if (numAmount >= 1000) {
          return '₹' + (numAmount / 1000).toFixed(1) + 'k'
        }
        return '₹' + numAmount.toLocaleString()
      } catch (error) {
        console.error('Error formatting amount:', error)
        return '₹' + amount
      }
    },

    // 图表自适应
    resizeChart () {
      if (this.chart) {
        try {
          this.chart.forceFit()
        } catch (error) {
          console.error('Error resizing OrderSevenTrend chart:', error)
        }
      }
    },

    // 处理类型变化
    handleTypeChange () {
      this.$emit('handleSevenTrendTypeChange', this.orderType)
    }
  }
}
</script>

<style lang="less" scoped>
.order-seven-trend-chart {
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;

  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .chart-title {
      margin: 0;
      font-size: 16px;
      font-weight: 500;
      color: #333;
    }
  }

  .chart-container {
    width: 100%;
    position: relative;

    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 300px;
    }

    .no-data-container {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 300px;
    }
  }
}
</style>
